import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { SessionResponseDto } from "@madamuh/domain";
import { ApiError } from "../../shared/api/client";
import { fetchFamilyDashboard } from "../../shared/api/family";
import { fetchCurrentSession } from "../../shared/api/session";
import { getEnv } from "../../shared/lib/env";
import {
  buildAbsoluteLiffRedirectUri,
  hasLiffCredentialParams,
  listUrlSearchKeys,
  LiffLoginIncompleteError,
  LiffLoginRedirectError,
  getIdTokenSafe,
  getLiffRuntimeState,
  isMiniAppPermanentLinkUrl,
  loginWithRedirect,
  type LiffRuntimeState,
  readLiffStateReturnTo,
  redactSensitiveUrlForDiagnostics
} from "../../shared/lib/liff";
import {
  clearFlowLoginStartedAt,
  clearFlowCounters,
  clearFlowTransientState,
  getFlowCounter,
  readFlowLoginStartedAt,
  incrementFlowCounter,
  readFlowReturnTo,
  readFlowToken,
  rememberFlowLoginStartedAt,
  rememberFlowReturnTo,
  rememberFlowTenantSlug,
  rememberFlowToken
} from "../../shared/session/flowStorage";
import { createPageErrorState, formatApiError, type PageErrorState } from "../../shared/session/flowSupport";
import { classifyStudentInviteFailure } from "../../shared/session/inviteFlowFailure";
import { canReuseExistingTenantSessionForEntry } from "../../shared/session/entryOwnerOnboarding";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

const STUDENT_SELF_INVITE_FLOW_NAMESPACE = "studentSelfInvite" as const;

type StudentInvitePhase =
  | "idle"
  | "initializing-liff"
  | "requesting-line-login"
  | "establishing-session"
  | "consuming-student-invite"
  | "refreshing-session"
  | "completed"
  | "redirecting"
  | "error";

type StudentInviteDiagnosticEvent = {
  at: string;
  label: string;
  detail?: string;
};

type StudentInviteDiagnosticsState = {
  bootstrapRunCount: number;
  lineLoginAttemptCount: number;
  lineLoginSuccessCount: number;
  liffInitCount: number;
  liffLoginCallCount: number;
  postRedirectInitCount: number;
  liffInitStartedAt: string | null;
  liffInitTimedOut: boolean;
  sessionRecoveryAttemptCount: number;
  sessionRecoverySuccessCount: number;
  recoveredSessionRoles: string[];
  recoveredEntryPath: string | null;
  consumeAttemptCount: number;
  consumeSuccessCount: number;
  consumeWasIdempotent: boolean;
  consumeCompletedBySessionRecovery: boolean;
  redirectCount: number;
  reusedExistingSessionCount: number;
  loginDispatched: boolean;
  finalRedirectTarget: string | null;
  autoRedirectAttempted: boolean;
  autoRedirectFailed: boolean;
  activeSelfLinkDetected: boolean;
  clearedStorageKeys: string[];
  lastConsumeStatus: string | null;
  lastConsumeErrorCode: string | null;
  lastConsumeErrorMessage: string | null;
  lastSessionStatus: string;
  lastSessionEntryPath: string | null;
  lastSessionRoles: string[];
  lastBackendErrorCode: string | null;
  lastBackendErrorMessage: string | null;
  lastLiffError: string | null;
  lastBackendError: string | null;
  events: StudentInviteDiagnosticEvent[];
};

function createStudentInviteDiagnosticsState(): StudentInviteDiagnosticsState {
  return {
    bootstrapRunCount: 0,
    lineLoginAttemptCount: 0,
    lineLoginSuccessCount: 0,
    liffInitCount: 0,
    liffLoginCallCount: 0,
    postRedirectInitCount: 0,
    liffInitStartedAt: null,
    liffInitTimedOut: false,
    sessionRecoveryAttemptCount: 0,
    sessionRecoverySuccessCount: 0,
    recoveredSessionRoles: [],
    recoveredEntryPath: null,
    consumeAttemptCount: 0,
    consumeSuccessCount: 0,
    consumeWasIdempotent: false,
    consumeCompletedBySessionRecovery: false,
    redirectCount: 0,
    reusedExistingSessionCount: 0,
    loginDispatched: false,
    finalRedirectTarget: null,
    autoRedirectAttempted: false,
    autoRedirectFailed: false,
    activeSelfLinkDetected: false,
    clearedStorageKeys: [],
    lastConsumeStatus: null,
    lastConsumeErrorCode: null,
    lastConsumeErrorMessage: null,
    lastSessionStatus: "idle",
    lastSessionEntryPath: null,
    lastSessionRoles: [],
    lastBackendErrorCode: null,
    lastBackendErrorMessage: null,
    lastLiffError: null,
    lastBackendError: null,
    events: []
  };
}

function buildStudentInvitePath(tenantSlug: string, token: string): string {
  return `/t/${tenantSlug}/student-invite${token ? `?token=${encodeURIComponent(token)}` : ""}`;
}

const STUDENT_SELF_INVITE_CANONICAL_STORAGE_KEYS = [
  "madamuh:studentSelfInvite:token",
  "madamuh:studentSelfInvite:tenantSlug",
  "madamuh:studentSelfInvite:returnTo",
  "madamuh:studentSelfInvite:loginAttemptCount",
  "madamuh:studentSelfInvite:loginStartedAt",
  "madamuh:studentSelfInvite:backendRecoveryAttemptCount",
  "madamuh:studentSelfInvite:miniappRedirectAttempted",
  "madamuh:studentInvite:miniappRedirectAttempted"
] as const;

function parseHttpUrl(value: string): URL | null {
  try {
    return value ? new URL(value) : null;
  } catch {
    return null;
  }
}

type ParsedStudentInviteRoute = {
  tenantSlug: string;
  path: string;
  token: string;
};

function parseStudentInviteRoute(candidate: string): ParsedStudentInviteRoute | null {
  if (!candidate) {
    return null;
  }

  try {
    const url = candidate.startsWith("http://") || candidate.startsWith("https://")
      ? new URL(candidate)
      : new URL(candidate, window.location.origin);
    const match = url.pathname.match(/^\/t\/([^/]+)\/student-invite$/);

    if (!match) {
      return null;
    }

    const token = url.searchParams.get("token")?.trim() ?? "";
    const tenantSlug = match[1] ?? "";

    return {
      tenantSlug,
      path: buildStudentInvitePath(tenantSlug, token),
      token
    };
  } catch {
    return null;
  }
}

function resolveStudentInviteTokenSource(
  tokenFromQuery: string,
  tokenFromLiffState: string,
  storedToken: string
): "query" | "liff.state" | "sessionStorage" | "none" {
  if (tokenFromQuery) {
    return "query";
  }

  if (tokenFromLiffState) {
    return "liff.state";
  }

  if (storedToken) {
    return "sessionStorage";
  }

  return "none";
}

function resolveStudentInviteRedirectUri(
  appPublicOrigin: string,
  tenantSlug: string,
  inviteToken: string,
  currentUrl: string,
  storedReturnTo: string
): string {
  const fallbackPath = buildStudentInvitePath(tenantSlug, inviteToken);
  const preferredAppOrigin = appPublicOrigin.trim();
  const hasConfiguredAppOrigin = preferredAppOrigin.length > 0;
  const preferredAppUrl = hasConfiguredAppOrigin ? parseHttpUrl(preferredAppOrigin) : null;
  const candidateUrl = preferredAppUrl
    ? new URL(fallbackPath, preferredAppUrl).toString()
    : currentUrl || storedReturnTo || fallbackPath;
  const absoluteUrl = new URL(buildAbsoluteLiffRedirectUri(candidateUrl));

  if (inviteToken) {
    absoluteUrl.searchParams.set("token", inviteToken);
  }

  return buildAbsoluteLiffRedirectUri(absoluteUrl.toString());
}

function resolveStudentInviteRedirectTarget(
  tenantSlug: string,
  consumedSession: SessionResponseDto | null,
  refreshedSession: SessionResponseDto | null
): string {
  const candidateEntryPath =
    consumedSession?.entryPath || refreshedSession?.entryPath || `/t/${tenantSlug}/home`;

  return candidateEntryPath.trim() || `/t/${tenantSlug}/home`;
}

function renderStudentInviteDiagnostics(
  diagnostics: StudentInviteDiagnosticsState,
  invitePhase: StudentInvitePhase,
  sessionStatus: string,
  env: ReturnType<typeof getEnv>,
  inviteToken: string,
  tokenFromLiffState: string,
  hasTokenQueryParam: boolean,
  storedToken: string,
  currentUrl: string,
  currentPath: string,
  currentSearch: string,
  liffStateReturnTo: string,
  parsedLiffStatePath: string | null,
  parsedLiffStateHasToken: boolean,
  redirectUriUsed: string
) {
  const tokenSource = resolveStudentInviteTokenSource(
    hasTokenQueryParam ? inviteToken : "",
    tokenFromLiffState,
    storedToken
  );
  const redirectUri = redirectUriUsed ? new URL(redirectUriUsed) : null;
  const configuredAppOrigin = env.appPublicOrigin.trim();
  const configuredAppOriginUrl = configuredAppOrigin ? parseHttpUrl(configuredAppOrigin) : null;
  const liffEndpointCompatibilityWarning =
    configuredAppOriginUrl && redirectUri && redirectUri.origin !== configuredAppOriginUrl.origin
      ? "redirectUri origin does not match VITE_APP_PUBLIC_ORIGIN"
      : !configuredAppOriginUrl && isMiniAppPermanentLinkUrl(currentUrl)
        ? "VITE_APP_PUBLIC_ORIGIN is not configured while using a LINE Mini App permanent link"
        : redirectUri?.origin === "https://miniapp.line.me"
          ? "redirectUri still points to miniapp.line.me"
          : null;

  return JSON.stringify(
    {
      tenantSlug:
        typeof window !== "undefined"
          ? window.location.pathname.split("/")[2] ?? "unknown-tenant"
          : "unknown-tenant",
      appEnv: env.appEnv,
      liffId: env.lineMiniAppLiffId || null,
      isInClient:
        typeof window !== "undefined" && typeof window.liff?.isInClient === "function"
          ? window.liff.isInClient()
          : null,
      isLoggedIn:
        typeof window !== "undefined" && typeof window.liff?.isLoggedIn === "function"
          ? window.liff.isLoggedIn()
          : null,
      hasIdToken:
        typeof window !== "undefined" && typeof window.liff?.getIDToken === "function"
          ? Boolean(window.liff.getIDToken())
          : null,
      hasInviteToken: Boolean(inviteToken),
      hasTokenQueryParam,
      hasStoredToken: Boolean(storedToken),
      tokenSource,
      currentPath,
      currentSearchKeys: listUrlSearchKeys(currentSearch),
      currentSearch: redactSensitiveUrlForDiagnostics(`${currentPath}${currentSearch}`)?.replace(currentPath, "") ?? "",
      hasLiffState: Boolean(liffStateReturnTo),
      parsedLiffStatePath: parsedLiffStatePath ? redactSensitiveUrlForDiagnostics(parsedLiffStatePath) : null,
      parsedLiffStateHasToken,
      hasLiffCredentialParams: hasLiffCredentialParams(currentSearch),
      currentUrl: redactSensitiveUrlForDiagnostics(currentUrl),
      redirectUriUsed: redactSensitiveUrlForDiagnostics(redirectUriUsed),
      redirectUriOrigin: redirectUri?.origin ?? null,
      redirectUriPath: redirectUri ? `${redirectUri.pathname}${redirectUri.search}` : null,
      redirectUriHasToken: redirectUri?.searchParams.has("token") ?? false,
      liffEndpointCompatibilityWarning,
      usingMiniAppPermanentLink: isMiniAppPermanentLinkUrl(currentUrl),
      invitePhase,
      sessionStatus,
      loginAttemptCount: getFlowCounter(STUDENT_SELF_INVITE_FLOW_NAMESPACE, "loginAttemptCount"),
      backendRecoveryAttemptCount: getFlowCounter(STUDENT_SELF_INVITE_FLOW_NAMESPACE, "backendRecoveryAttemptCount"),
      bootstrapRunCount: diagnostics.bootstrapRunCount,
      lineLoginAttemptCount: diagnostics.lineLoginAttemptCount,
      lineLoginSuccessCount: diagnostics.lineLoginSuccessCount,
      liffInitCount: diagnostics.liffInitCount,
      liffLoginCallCount: diagnostics.liffLoginCallCount,
      postRedirectInitCount: diagnostics.postRedirectInitCount,
      liffInitStartedAt: diagnostics.liffInitStartedAt,
      liffInitTimedOut: diagnostics.liffInitTimedOut,
      sessionRecoveryAttemptCount: diagnostics.sessionRecoveryAttemptCount,
      sessionRecoverySuccessCount: diagnostics.sessionRecoverySuccessCount,
      recoveredSessionRoles: diagnostics.recoveredSessionRoles,
      recoveredEntryPath: diagnostics.recoveredEntryPath,
      consumeAttemptCount: diagnostics.consumeAttemptCount,
      consumeSuccessCount: diagnostics.consumeSuccessCount,
      consumeWasIdempotent: diagnostics.consumeWasIdempotent,
      consumeCompletedBySessionRecovery: diagnostics.consumeCompletedBySessionRecovery,
      redirectCount: diagnostics.redirectCount,
      reusedExistingSessionCount: diagnostics.reusedExistingSessionCount,
      loginDispatched: diagnostics.loginDispatched,
      finalRedirectTarget: diagnostics.finalRedirectTarget,
      autoRedirectAttempted: diagnostics.autoRedirectAttempted,
      autoRedirectFailed: diagnostics.autoRedirectFailed,
      activeSelfLinkDetected: diagnostics.activeSelfLinkDetected,
      clearedStorageKeys: diagnostics.clearedStorageKeys,
      lastConsumeStatus: diagnostics.lastConsumeStatus,
      lastConsumeErrorCode: diagnostics.lastConsumeErrorCode,
      lastConsumeErrorMessage: diagnostics.lastConsumeErrorMessage,
      lastSessionStatus: diagnostics.lastSessionStatus,
      lastSessionEntryPath: diagnostics.lastSessionEntryPath,
      lastSessionRoles: diagnostics.lastSessionRoles,
      lastBackendErrorCode: diagnostics.lastBackendErrorCode,
      lastBackendErrorMessage: diagnostics.lastBackendErrorMessage,
      lastLiffError: diagnostics.lastLiffError,
      lastBackendError: diagnostics.lastBackendError,
      events: diagnostics.events
    },
    null,
    2
  );
}

export function StudentInvitePage() {
  const env = getEnv();
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { status, session, errorMessage, establishSessionWithLineToken, consumeStudentSelfInviteToken } =
    useSessionBootstrap();
  const [searchParams] = useSearchParams();
  const [invitePhase, setInvitePhase] = useState<StudentInvitePhase>("idle");
  const [inviteErrorState, setInviteErrorState] = useState<PageErrorState>(createPageErrorState);
  const [retryNonce, setRetryNonce] = useState(0);
  const [diagnostics, setDiagnostics] = useState<StudentInviteDiagnosticsState>(createStudentInviteDiagnosticsState);
  const latestSessionRef = useRef(session);
  const bootstrapRunKeyRef = useRef<string | null>(null);
  const completionCommittedRef = useRef(false);
  const tokenFromQuery = searchParams.get("token")?.trim() ?? "";
  const hasTokenQueryParam = searchParams.has("token");
  const liffStateReturnTo = readLiffStateReturnTo(typeof window !== "undefined" ? window.location.search : "");
  const parsedLiffStateRoute = liffStateReturnTo ? parseStudentInviteRoute(liffStateReturnTo) : null;
  const tokenFromLiffState = parsedLiffStateRoute?.token ?? "";
  const storedToken = readFlowToken(STUDENT_SELF_INVITE_FLOW_NAMESPACE, tenantSlug);
  const storedReturnTo = readFlowReturnTo(STUDENT_SELF_INVITE_FLOW_NAMESPACE) ?? "";
  const inviteToken = tokenFromQuery || tokenFromLiffState || storedToken || "";
  const tenantTopPath = `/t/${tenantSlug}`;
  const currentBrowserUrl = typeof window !== "undefined" ? window.location.href : "";
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
  const currentSearch = typeof window !== "undefined" ? window.location.search : "";
  const redirectUri = resolveStudentInviteRedirectUri(
    env.appPublicOrigin,
    tenantSlug,
    inviteToken,
    currentBrowserUrl,
    storedReturnTo
  );

  const pushDiagnosticEvent = useRef((label: string, detail?: string) => {
    setDiagnostics((current) => ({
      ...current,
      events: [
        ...current.events,
        detail === undefined
          ? { at: new Date().toISOString(), label }
          : { at: new Date().toISOString(), label, detail }
      ].slice(-12)
    }));
  }).current;

  useEffect(() => {
    latestSessionRef.current = session;
    setDiagnostics((current) => ({
      ...current,
      lastSessionStatus: status,
      lastSessionEntryPath: session?.entryPath ?? null,
      lastSessionRoles: session?.roles ?? [],
      lastBackendErrorMessage: errorMessage,
      lastBackendErrorCode:
        errorMessage && errorMessage.startsWith("HTTP ")
          ? errorMessage.split("\n").find((line) => line.startsWith("code: "))?.slice(6) ?? null
          : current.lastBackendErrorCode
    }));
  }, [errorMessage, session, status]);

  function handleReturnToTenantTop() {
    completionCommittedRef.current = false;
    setDiagnostics((current) => ({
      ...current,
      clearedStorageKeys: [...STUDENT_SELF_INVITE_CANONICAL_STORAGE_KEYS]
    }));
    clearFlowTransientState(STUDENT_SELF_INVITE_FLOW_NAMESPACE);
    window.sessionStorage.removeItem("madamuh:studentSelfInvite:miniappRedirectAttempted");
    window.sessionStorage.removeItem("madamuh:studentInvite:miniappRedirectAttempted");
    setInviteErrorState(createPageErrorState());
    setDiagnostics(createStudentInviteDiagnosticsState());
    bootstrapRunKeyRef.current = null;
    navigate(tenantTopPath, { replace: true });
  }

  function handleRetryLogin() {
    completionCommittedRef.current = false;
    clearFlowCounters(STUDENT_SELF_INVITE_FLOW_NAMESPACE);
    clearFlowLoginStartedAt(STUDENT_SELF_INVITE_FLOW_NAMESPACE);
    setInviteErrorState(createPageErrorState());
    setDiagnostics(createStudentInviteDiagnosticsState());
    bootstrapRunKeyRef.current = null;
    setInvitePhase("idle");
    setRetryNonce((current) => current + 1);
  }

  function clearStudentInviteTransientStateWithDiagnostics() {
    setDiagnostics((current) => ({
      ...current,
      clearedStorageKeys: [...STUDENT_SELF_INVITE_CANONICAL_STORAGE_KEYS]
    }));
    clearFlowTransientState(STUDENT_SELF_INVITE_FLOW_NAMESPACE);
    window.sessionStorage.removeItem("madamuh:studentSelfInvite:miniappRedirectAttempted");
    window.sessionStorage.removeItem("madamuh:studentInvite:miniappRedirectAttempted");
  }

  function resolveHomeTarget(finalRedirectTarget?: string | null): string {
    const candidate = finalRedirectTarget?.trim() || latestSessionRef.current?.entryPath?.trim() || "";
    return candidate || `/t/${tenantSlug}/home`;
  }

  function completeStudentInviteFlow(params: {
    finalRedirectTarget: string;
    consumeWasIdempotent?: boolean;
    consumeCompletedBySessionRecovery?: boolean;
    activeSelfLinkDetected?: boolean;
    incrementConsumeSuccess?: boolean;
    lastConsumeStatus?: string;
  }) {
    completionCommittedRef.current = true;
    clearStudentInviteTransientStateWithDiagnostics();
    setInviteErrorState(createPageErrorState());
    setInvitePhase("completed");
    setDiagnostics((current) => ({
      ...current,
      consumeSuccessCount:
        params.incrementConsumeSuccess === false ? current.consumeSuccessCount : current.consumeSuccessCount + 1,
      consumeWasIdempotent: params.consumeWasIdempotent ?? current.consumeWasIdempotent,
      consumeCompletedBySessionRecovery:
        params.consumeCompletedBySessionRecovery ?? current.consumeCompletedBySessionRecovery,
      activeSelfLinkDetected: params.activeSelfLinkDetected ?? current.activeSelfLinkDetected,
      finalRedirectTarget: params.finalRedirectTarget,
      lastConsumeStatus: params.lastConsumeStatus ?? current.lastConsumeStatus
    }));
    pushDiagnosticEvent(
      "student-invite-completed",
      JSON.stringify({
        target: params.finalRedirectTarget,
        consumeCompletedBySessionRecovery: params.consumeCompletedBySessionRecovery ?? false,
        activeSelfLinkDetected: params.activeSelfLinkDetected ?? false
      })
    );
  }

  function handleNavigateHome() {
    const finalRedirectTarget = resolveHomeTarget(diagnostics.finalRedirectTarget);
    setDiagnostics((current) => ({
      ...current,
      redirectCount: current.redirectCount + 1,
      finalRedirectTarget
    }));
    pushDiagnosticEvent("navigate-home", finalRedirectTarget);
    navigate(finalRedirectTarget, { replace: true });
  }

  async function detectActiveSelfLinkAccess(): Promise<boolean> {
    try {
      const dashboard = await fetchFamilyDashboard(tenantSlug);
      return dashboard.studentSummaries.some((student) => student.primaryRelationType === "self");
    } catch (error) {
      setDiagnostics((current) => ({
        ...current,
        lastBackendError: formatApiError(error)
      }));
      pushDiagnosticEvent("self-link-detect-error", formatApiError(error));
      return false;
    }
  }

  async function recoverStudentInviteSession(reason: string): Promise<SessionResponseDto | null> {
    setDiagnostics((current) => ({
      ...current,
      sessionRecoveryAttemptCount: current.sessionRecoveryAttemptCount + 1
    }));
    pushDiagnosticEvent("session-recovery-start", reason);

    try {
      const recoveredSession = await fetchCurrentSession();

      if (!canReuseExistingTenantSessionForEntry(tenantSlug, recoveredSession)) {
        pushDiagnosticEvent(
          "session-recovery-ignored",
          JSON.stringify({
            recoveredTenantSlug: recoveredSession.tenant.tenantSlug,
            expectedTenantSlug: tenantSlug
          })
        );
        return null;
      }

      setDiagnostics((current) => ({
        ...current,
        sessionRecoverySuccessCount: current.sessionRecoverySuccessCount + 1,
        recoveredSessionRoles: recoveredSession.roles,
        recoveredEntryPath: recoveredSession.entryPath
      }));
      clearFlowLoginStartedAt(STUDENT_SELF_INVITE_FLOW_NAMESPACE);
      pushDiagnosticEvent(
        "session-recovery-success",
        JSON.stringify({
          entryPath: recoveredSession.entryPath,
          roles: recoveredSession.roles
        })
      );
      return recoveredSession;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        pushDiagnosticEvent("session-recovery-miss", reason);
        return null;
      }

      setDiagnostics((current) => ({
        ...current,
        lastBackendError: formatApiError(error)
      }));
      pushDiagnosticEvent("session-recovery-error", formatApiError(error));
      return null;
    }
  }

  useEffect(() => {
    const bootstrapRunKey = JSON.stringify({
      tenantSlug,
      hasInviteToken: Boolean(inviteToken),
      hasTokenQueryParam,
      retryNonce
    });

    if (bootstrapRunKeyRef.current === bootstrapRunKey) {
      return;
    }

    bootstrapRunKeyRef.current = bootstrapRunKey;
    completionCommittedRef.current = false;
    setDiagnostics((current) => ({
      ...current,
      bootstrapRunCount: current.bootstrapRunCount + 1
    }));
    pushDiagnosticEvent("bootstrap-start", bootstrapRunKey);

    let cancelled = false;

    async function runFlow() {
      async function consumeInviteAndRedirect(
        existingSession: SessionResponseDto | null,
        consumeSource: string
      ): Promise<void> {
        setInvitePhase("consuming-student-invite");
        setDiagnostics((current) => ({
          ...current,
          consumeAttemptCount: current.consumeAttemptCount + 1,
          lastConsumeStatus: "pending",
          lastConsumeErrorCode: null,
          lastConsumeErrorMessage: null
        }));
        pushDiagnosticEvent(
          "student-invite-consume-start",
          JSON.stringify({
            source: consumeSource,
            hasExistingSession: Boolean(existingSession),
            rolesBeforeConsume: existingSession?.roles ?? []
          })
        );

        const consumedSession = await consumeStudentSelfInviteToken(tenantSlug, inviteToken);

        if (cancelled || completionCommittedRef.current) {
          return;
        }

        setInvitePhase("refreshing-session");
        const refreshedSession = await recoverStudentInviteSession("post-consume-refresh");

        if (cancelled) {
          return;
        }

        const finalRedirectTarget = resolveStudentInviteRedirectTarget(tenantSlug, consumedSession, refreshedSession);
        setDiagnostics((current) => ({
          ...current,
          consumeWasIdempotent: current.consumeWasIdempotent,
          lastConsumeStatus: "success",
          finalRedirectTarget
        }));
        pushDiagnosticEvent(
          "student-invite-consume-success",
          JSON.stringify({
            source: consumeSource,
            entryPath: consumedSession.entryPath,
            roles: consumedSession.roles,
            refreshedEntryPath: refreshedSession?.entryPath ?? null
          })
        );
        completeStudentInviteFlow({
          finalRedirectTarget,
          lastConsumeStatus: "success"
        });
      }

      if (hasTokenQueryParam && !tokenFromQuery) {
        setInvitePhase("error");
        setInviteErrorState({
          message: "生徒本人用招待 token が見つかりません。招待URL全体を開き直すか、新しい招待を利用してください。",
          debugMessage: null,
          allowRetry: false
        });
        clearFlowTransientState(STUDENT_SELF_INVITE_FLOW_NAMESPACE);
        return;
      }

      if (!inviteToken) {
        setInvitePhase("error");
        setInviteErrorState({
          message: "生徒本人用招待 token がありません。招待URL全体を開き直すか、新しい招待を利用してください。",
          debugMessage: null,
          allowRetry: false
        });
        return;
      }

      rememberFlowToken(STUDENT_SELF_INVITE_FLOW_NAMESPACE, inviteToken);
      rememberFlowTenantSlug(STUDENT_SELF_INVITE_FLOW_NAMESPACE, tenantSlug);
      rememberFlowReturnTo(STUDENT_SELF_INVITE_FLOW_NAMESPACE, redirectUri);
      setInviteErrorState(createPageErrorState());
      setInvitePhase("initializing-liff");
      setDiagnostics((current) => ({
        ...current,
        loginDispatched: false,
        lastLiffError: null,
        lastBackendError: null
      }));

      try {
        let nextSession = latestSessionRef.current;
        const liffInitTimeoutMs = 3_500;

        if (!canReuseExistingTenantSessionForEntry(tenantSlug, nextSession)) {
          nextSession = await recoverStudentInviteSession("initial-load");
        }

        if (canReuseExistingTenantSessionForEntry(tenantSlug, nextSession)) {
          clearFlowLoginStartedAt(STUDENT_SELF_INVITE_FLOW_NAMESPACE);
          setDiagnostics((current) => ({
            ...current,
            reusedExistingSessionCount: current.reusedExistingSessionCount + 1,
            loginDispatched: false
          }));
          pushDiagnosticEvent(
            "reuse-existing-session",
            JSON.stringify({
              entryPath: nextSession?.entryPath ?? null,
              roles: nextSession?.roles ?? []
            })
          );
          await consumeInviteAndRedirect(nextSession, "recovered-session");
          return;
        }

        {
          const loginAttemptCount = getFlowCounter(STUDENT_SELF_INVITE_FLOW_NAMESPACE, "loginAttemptCount");
          setDiagnostics((current) => ({
            ...current,
            liffInitCount: current.liffInitCount + 1,
            liffInitStartedAt: new Date().toISOString(),
            liffInitTimedOut: false,
            postRedirectInitCount:
              loginAttemptCount > 0 ? current.postRedirectInitCount + 1 : current.postRedirectInitCount
          }));
          let runtimeState: LiffRuntimeState | null = null;

          try {
            runtimeState = await Promise.race([
              getLiffRuntimeState(env.lineMiniAppLiffId, {
                timeoutMs: loginAttemptCount > 0 ? 2_000 : 0,
                intervalMs: 150
              }),
              new Promise<never>((_, reject) => {
                window.setTimeout(() => reject(new Error("LIFF init timed out")), liffInitTimeoutMs);
              })
            ]);
          } catch (error) {
            if (error instanceof Error && error.message === "LIFF init timed out") {
              setDiagnostics((current) => ({
                ...current,
                liffInitTimedOut: true,
                lastLiffError: error.message
              }));
              pushDiagnosticEvent("liff-init-timeout", String(liffInitTimeoutMs));
              nextSession = await recoverStudentInviteSession("liff-init-timeout");

              if (!nextSession) {
                throw new LiffLoginIncompleteError();
              }
            } else {
              throw error;
            }
          }

          if (nextSession) {
            runtimeState = null;
          }

          if (!nextSession && runtimeState && !runtimeState.isLoggedIn) {
            if (loginAttemptCount >= 1) {
              throw new LiffLoginIncompleteError();
            }

            incrementFlowCounter(STUDENT_SELF_INVITE_FLOW_NAMESPACE, "loginAttemptCount");
            rememberFlowLoginStartedAt(STUDENT_SELF_INVITE_FLOW_NAMESPACE, Date.now());
            setDiagnostics((current) => ({
              ...current,
              lineLoginAttemptCount: current.lineLoginAttemptCount + 1,
              liffLoginCallCount: current.liffLoginCallCount + 1,
              loginDispatched: true
            }));
            pushDiagnosticEvent(
              "line-login-start",
              JSON.stringify({
                tenantSlug,
                isInClient: runtimeState.isInClient,
                redirectUri: redactSensitiveUrlForDiagnostics(redirectUri)
              })
            );
            pushDiagnosticEvent(
              "login-dispatched",
              JSON.stringify({
                tenantSlug,
                isInClient: runtimeState.isInClient,
                redirectUri: redactSensitiveUrlForDiagnostics(redirectUri)
              })
            );
            setInvitePhase("requesting-line-login");
            await loginWithRedirect(env.lineMiniAppLiffId, redirectUri);
            return;
          }

          const idToken = nextSession
            ? null
            : await getIdTokenSafe(env.lineMiniAppLiffId, {
                timeoutMs: runtimeState?.isInClient === false ? 2_500 : 1_500,
                intervalMs: 150
              });

          if (!nextSession && !idToken) {
            if (loginAttemptCount >= 1) {
              throw new LiffLoginIncompleteError();
            }

            incrementFlowCounter(STUDENT_SELF_INVITE_FLOW_NAMESPACE, "loginAttemptCount");
            rememberFlowLoginStartedAt(STUDENT_SELF_INVITE_FLOW_NAMESPACE, Date.now());
            setDiagnostics((current) => ({
              ...current,
              lineLoginAttemptCount: current.lineLoginAttemptCount + 1,
              liffLoginCallCount: current.liffLoginCallCount + 1,
              loginDispatched: true
            }));
            pushDiagnosticEvent(
              "line-login-start",
              JSON.stringify({
                tenantSlug,
                isInClient: runtimeState?.isInClient ?? null,
                reason: "missing-id-token",
                redirectUri: redactSensitiveUrlForDiagnostics(redirectUri)
              })
            );
            pushDiagnosticEvent(
              "login-dispatched",
              JSON.stringify({
                tenantSlug,
                isInClient: runtimeState?.isInClient ?? null,
                reason: "missing-id-token",
                redirectUri: redactSensitiveUrlForDiagnostics(redirectUri)
              })
            );
            setInvitePhase("requesting-line-login");
            await loginWithRedirect(env.lineMiniAppLiffId, redirectUri);
            return;
          }

          if (cancelled) {
            return;
          }

          if (!nextSession && idToken) {
            clearFlowLoginStartedAt(STUDENT_SELF_INVITE_FLOW_NAMESPACE);
            setDiagnostics((current) => ({
              ...current,
              lineLoginSuccessCount: current.lineLoginSuccessCount + 1,
              loginDispatched: false
            }));
            pushDiagnosticEvent("line-login-success");
            setInvitePhase("establishing-session");
            nextSession = await establishSessionWithLineToken(tenantSlug, idToken);
          }
        }

        if (cancelled || completionCommittedRef.current) {
          return;
        }

        await consumeInviteAndRedirect(nextSession, "post-line-login");
      } catch (error) {
        if (cancelled || completionCommittedRef.current) {
          return;
        }

        if (error instanceof LiffLoginRedirectError) {
          return;
        }

        if (error instanceof ApiError && error.code === "INVALID_LINE_TOKEN") {
          const backendRecoveryAttemptCount = getFlowCounter(
            STUDENT_SELF_INVITE_FLOW_NAMESPACE,
            "backendRecoveryAttemptCount"
          );

          if (backendRecoveryAttemptCount < 1) {
            incrementFlowCounter(STUDENT_SELF_INVITE_FLOW_NAMESPACE, "backendRecoveryAttemptCount");
            rememberFlowLoginStartedAt(STUDENT_SELF_INVITE_FLOW_NAMESPACE, Date.now());
            setDiagnostics((current) => ({
              ...current,
              lineLoginAttemptCount: current.lineLoginAttemptCount + 1,
              liffLoginCallCount: current.liffLoginCallCount + 1,
              loginDispatched: true
            }));
            pushDiagnosticEvent("line-reauth-start", redactSensitiveUrlForDiagnostics(redirectUri) ?? redirectUri);
            setInvitePhase("requesting-line-login");
            await loginWithRedirect(env.lineMiniAppLiffId, redirectUri);
            return;
          }
        }

        const isInviteExpired = error instanceof ApiError && error.code === "STUDENT_SELF_INVITE_EXPIRED";
        const isInviteRevoked = error instanceof ApiError && error.code === "STUDENT_SELF_INVITE_REVOKED";
        const isInviteAlreadyUsed = error instanceof ApiError && error.code === "STUDENT_SELF_INVITE_ALREADY_USED";
        const isInviteMissing = error instanceof ApiError && error.code === "STUDENT_SELF_INVITE_NOT_FOUND";

        if (isInviteExpired || isInviteRevoked || isInviteAlreadyUsed || isInviteMissing) {
          clearFlowTransientState(STUDENT_SELF_INVITE_FLOW_NAMESPACE);
        }

        clearFlowLoginStartedAt(STUDENT_SELF_INVITE_FLOW_NAMESPACE);
        const studentInviteFailure = classifyStudentInviteFailure(error);
        setDiagnostics((current) => ({
          ...current,
          loginDispatched: false,
          lastConsumeStatus:
            current.lastConsumeStatus === "pending" && error instanceof ApiError
              ? String(error.status)
              : current.lastConsumeStatus,
          lastConsumeErrorCode: error instanceof ApiError ? error.code : current.lastConsumeErrorCode,
          lastConsumeErrorMessage: error instanceof ApiError ? formatApiError(error) : current.lastConsumeErrorMessage,
          lastLiffError: error instanceof ApiError ? null : formatApiError(error),
          lastBackendError: error instanceof ApiError ? formatApiError(error) : null
        }));
        setInvitePhase("error");
        pushDiagnosticEvent("student-invite-error", formatApiError(error));
        setInviteErrorState({
          message: studentInviteFailure.userMessage,
          debugMessage: formatApiError(error),
          allowRetry: studentInviteFailure.allowRetry
        });
      }
    }

    void runFlow();

    return () => {
      cancelled = true;
    };
  }, [
    consumeStudentSelfInviteToken,
    currentBrowserUrl,
    redirectUri,
    env.lineMiniAppLiffId,
    establishSessionWithLineToken,
    hasTokenQueryParam,
    inviteToken,
    navigate,
    pushDiagnosticEvent,
    retryNonce,
    storedReturnTo,
    tenantSlug,
    tokenFromQuery
  ]);

  useEffect(() => {
    if (invitePhase !== "requesting-line-login") {
      return;
    }

    const loginStartedAt = readFlowLoginStartedAt(STUDENT_SELF_INVITE_FLOW_NAMESPACE);

    if (!loginStartedAt) {
      return;
    }

    const timeoutMs = 4_000;
    const remainingMs = Math.max(timeoutMs - (Date.now() - loginStartedAt), 0);
    const timer = window.setTimeout(() => {
      if (readFlowLoginStartedAt(STUDENT_SELF_INVITE_FLOW_NAMESPACE) !== loginStartedAt) {
        return;
      }

      clearFlowLoginStartedAt(STUDENT_SELF_INVITE_FLOW_NAMESPACE);
      setDiagnostics((current) => ({
        ...current,
        loginDispatched: false,
        lastLiffError: "LIFF login did not navigate"
      }));
      pushDiagnosticEvent("student-invite-error", "LIFF login did not navigate");
      setInvitePhase("error");
      setInviteErrorState({
        message: "LINEログインを開始できませんでした。LINEアプリからもう一度開いてください。",
        debugMessage: "LIFF login did not navigate",
        allowRetry: true
      });
    }, remainingMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [invitePhase, pushDiagnosticEvent]);

  useEffect(() => {
    if (invitePhase !== "completed") {
      return;
    }

    if (diagnostics.autoRedirectAttempted) {
      return;
    }

    const finalRedirectTarget = resolveHomeTarget(diagnostics.finalRedirectTarget);
    const timer = window.setTimeout(() => {
      setDiagnostics((current) => ({
        ...current,
        autoRedirectAttempted: true,
        redirectCount: current.redirectCount + 1,
        finalRedirectTarget
      }));
      pushDiagnosticEvent("auto-redirect", finalRedirectTarget);
      navigate(finalRedirectTarget, { replace: true });
    }, 1500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [diagnostics.autoRedirectAttempted, diagnostics.finalRedirectTarget, invitePhase, navigate, pushDiagnosticEvent, tenantSlug]);

  useEffect(() => {
    if (invitePhase !== "consuming-student-invite" && invitePhase !== "refreshing-session") {
      return;
    }

    if (completionCommittedRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      void (async () => {
        if (completionCommittedRef.current) {
          return;
        }

        pushDiagnosticEvent("consume-pending-timeout");
        const recoveredSession = await recoverStudentInviteSession("consume-pending-timeout");

        if (!recoveredSession || !recoveredSession.roles.includes("student")) {
          setInvitePhase("error");
          setInviteErrorState({
            message: "ログインに失敗しました。ホーム画面に戻り、もう一度お試しください。",
            debugMessage: "Student self invite consume did not complete",
            allowRetry: true
          });
          setDiagnostics((current) => ({
            ...current,
            lastConsumeStatus: current.lastConsumeStatus === "pending" ? "timeout" : current.lastConsumeStatus,
            lastConsumeErrorMessage: current.lastConsumeErrorMessage ?? "Student self invite consume did not complete"
          }));
          pushDiagnosticEvent("student-invite-error", "Student self invite consume did not complete");
          return;
        }

        const activeSelfLinkDetected = await detectActiveSelfLinkAccess();
        const finalRedirectTarget = resolveHomeTarget(recoveredSession.entryPath);
        setDiagnostics((current) => ({
          ...current,
          activeSelfLinkDetected
        }));

        if (!activeSelfLinkDetected) {
          setInvitePhase("error");
          setInviteErrorState({
            message: "ログインに失敗しました。ホーム画面に戻り、もう一度お試しください。",
            debugMessage: "Student self invite session recovered without active self access",
            allowRetry: true
          });
          setDiagnostics((current) => ({
            ...current,
            lastConsumeStatus: current.lastConsumeStatus === "pending" ? "timeout-no-self-link" : current.lastConsumeStatus,
            lastConsumeErrorMessage:
              current.lastConsumeErrorMessage ?? "Student self invite session recovered without active self access"
          }));
          pushDiagnosticEvent("student-invite-error", "Student self invite session recovered without active self access");
          return;
        }

        completeStudentInviteFlow({
          finalRedirectTarget,
          consumeCompletedBySessionRecovery: true,
          activeSelfLinkDetected: true,
          lastConsumeStatus: "completed-by-session-recovery"
        });
      })();
    }, 4000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [invitePhase, pushDiagnosticEvent, tenantSlug]);

  return (
    <PageScaffold
      title="生徒本人用招待"
      description="生徒本人用招待リンクです。LIFF で LINE login を確認し、token consume 後に自分のホームへ移動します。"
      links={[{ to: "/t/:tenantSlug/entry", label: "Entryへ戻る" }]}
    >
      {invitePhase === "initializing-liff" ? <p>LIFFを初期化しています。</p> : null}
      {invitePhase === "requesting-line-login" ? <p>LINEログインを確認しています。</p> : null}
      {invitePhase === "establishing-session" ? <p>セッションを作成しています。</p> : null}
      {invitePhase === "consuming-student-invite" ? <p>生徒本人の権限を設定しています。</p> : null}
      {invitePhase === "refreshing-session" ? <p>セッションを更新しています。</p> : null}
      {invitePhase === "completed" ? (
        <div style={{ display: "grid", gap: 8 }}>
          <p style={{ margin: 0 }}>登録が完了しました。</p>
          <p style={{ margin: 0 }}>ホーム画面へ進んでください。</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={handleNavigateHome}>
              ホームへ
            </button>
          </div>
        </div>
      ) : null}
      {invitePhase === "redirecting" ? <p>ホームへ移動しています。</p> : null}
      {invitePhase === "error" ? (
        <div style={{ display: "grid", gap: 8 }}>
          <p style={{ margin: 0 }}>
            {inviteErrorState.message ?? "ログインに失敗しました。ホーム画面に戻り、もう一度お試しください。"}
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={handleReturnToTenantTop}>
              ホーム画面に戻る
            </button>
            {inviteErrorState.allowRetry ? (
              <button type="button" onClick={handleRetryLogin}>
                もう一度ログインする
              </button>
            ) : null}
          </div>
          {env.reviewDebugEnabled && inviteErrorState.debugMessage ? (
            <pre
              style={{
                margin: 0,
                padding: 12,
                borderRadius: 8,
                background: "#f6f6f6",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
              }}
            >
              {inviteErrorState.debugMessage}
            </pre>
          ) : null}
        </div>
      ) : null}
      {env.reviewDebugEnabled ? (
        <pre
          style={{
            margin: 0,
            padding: 12,
            borderRadius: 8,
            background: "#f6f6f6",
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word"
          }}
        >
          {renderStudentInviteDiagnostics(
            diagnostics,
            invitePhase,
            status,
            env,
            inviteToken,
            tokenFromLiffState,
            hasTokenQueryParam,
            storedToken ?? "",
            currentBrowserUrl,
            currentPath,
            currentSearch,
            liffStateReturnTo ?? "",
            parsedLiffStateRoute?.path ?? null,
            Boolean(parsedLiffStateRoute?.token),
            redirectUri
          )}
        </pre>
      ) : null}
    </PageScaffold>
  );
}
